import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer,
} from "recharts";
import {
  LayoutDashboard, BarChart3, Inbox, Globe, LogOut, Trash2,
  Eye, EyeOff, ExternalLink, Phone, MessageCircle,
} from "lucide-react";
import { adminFetch } from "@/pages/admin/AdminLogin";
import { BUSINESS, waLink } from "@/data/business";

const TABS = [
  { id: "overview", label: "Overview", icon: LayoutDashboard },
  { id: "analytics", label: "Analytics", icon: BarChart3 },
  { id: "leads", label: "Leads", icon: Inbox },
  { id: "website", label: "Website", icon: Globe },
];

const GOLD = "#C5A059";

function StatCard({ label, value, testid }) {
  return (
    <div className="rounded-2xl border border-ivory/10 bg-white/[0.03] p-5" data-testid={testid}>
      <p className="text-[10px] font-semibold uppercase tracking-[0.2em] text-ivory/45">{label}</p>
      <p className="mt-2 font-serif text-3xl font-medium text-ivory">{value}</p>
    </div>
  );
}

function SectionCard({ title, children, testid }) {
  return (
    <section className="rounded-2xl border border-ivory/10 bg-white/[0.03] p-5 sm:p-6" data-testid={testid}>
      <h3 className="text-[11px] font-semibold uppercase tracking-[0.24em] text-champagne">{title}</h3>
      <div className="mt-5">{children}</div>
    </section>
  );
}

function EmptyNote({ text }) {
  return (
    <p className="rounded-xl border border-dashed border-ivory/15 px-5 py-8 text-center text-sm text-ivory/40">
      {text}
    </p>
  );
}

export default function AdminDashboard() {
  const navigate = useNavigate();
  const [admin, setAdmin] = useState(null);
  const [tab, setTab] = useState("overview");
  const [days, setDays] = useState(30);
  const [analytics, setAnalytics] = useState(null);
  const [leads, setLeads] = useState(null);

  const loadAnalytics = useCallback(async (range) => {
    const res = await adminFetch(`/admin/analytics?days=${range}`);
    setAnalytics(await res.json());
  }, []);

  const loadLeads = useCallback(async () => {
    const res = await adminFetch(`/admin/enquiries`);
    setLeads(await res.json());
  }, []);

  useEffect(() => {
    adminFetch("/admin/me")
      .then((r) => r.json())
      .then(setAdmin)
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (!admin) return;
    loadAnalytics(days).catch(() => {});
    loadLeads().catch(() => {});
  }, [admin, days, loadAnalytics, loadLeads]);

  const logout = async () => {
    try { await adminFetch("/admin/logout", { method: "POST" }); } catch {}
    localStorage.removeItem("gg_admin_token");
    navigate("/admin/login", { replace: true });
  };

  const markRead = async (id, read) => {
    await adminFetch(`/admin/enquiries/${id}`, { method: "PATCH", body: JSON.stringify({ read }) });
    setLeads((ls) => ls.map((l) => (l.id === id ? { ...l, read } : l)));
  };

  const removeLead = async (id) => {
    if (!window.confirm("Delete this enquiry permanently?")) return;
    await adminFetch(`/admin/enquiries/${id}`, { method: "DELETE" });
    setLeads((ls) => ls.filter((l) => l.id !== id));
  };

  if (!admin) {
    return (
      <main className="flex min-h-screen items-center justify-center bg-ink" data-testid="admin-loading">
        <p className="text-xs font-semibold uppercase tracking-[0.24em] text-ivory/40">Loading dashboard…</p>
      </main>
    );
  }

  const unread = leads ? leads.filter((l) => !l.read).length : 0;

  return (
    <main className="min-h-screen bg-ink text-ivory" data-testid="admin-dashboard">
      <header className="sticky top-0 z-40 border-b border-ivory/10 bg-ink/95 backdrop-blur-md">
        <div className="mx-auto flex max-w-6xl items-center justify-between px-5 py-4">
          <div className="leading-none">
            <p className="font-serif text-xl italic">Glam Girl</p>
            <p className="mt-0.5 text-[9px] font-semibold uppercase tracking-[0.3em] text-champagne">Owner Dashboard</p>
          </div>
          <div className="flex items-center gap-4">
            <span className="hidden text-xs text-ivory/45 sm:block" data-testid="admin-email-label">{admin.email}</span>
            <button
              onClick={logout}
              data-testid="admin-logout-button"
              className="inline-flex items-center gap-1.5 rounded-full border border-ivory/15 px-4 py-2 text-xs font-semibold transition-colors hover:border-champagne hover:text-champagne"
            >
              <LogOut className="h-3.5 w-3.5" /> Logout
            </button>
          </div>
        </div>
        <nav className="mx-auto flex max-w-6xl gap-1 overflow-x-auto px-5 pb-3 no-scrollbar" aria-label="Dashboard sections">
          {TABS.map((t) => (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              data-testid={`admin-tab-${t.id}`}
              className={`inline-flex shrink-0 items-center gap-2 rounded-full px-4 py-2 text-xs font-semibold tracking-wide transition-colors ${
                tab === t.id ? "bg-champagne text-ink" : "text-ivory/55 hover:text-ivory"
              }`}
            >
              <t.icon className="h-3.5 w-3.5" />
              {t.label}
              {t.id === "leads" && unread > 0 && (
                <span className="rounded-full bg-rose-deep px-1.5 text-[10px] text-ivory" data-testid="leads-unread-badge">{unread}</span>
              )}
            </button>
          ))}
        </nav>
      </header>

      <div className="mx-auto max-w-6xl space-y-5 px-5 py-8">
        {(tab === "overview" || tab === "analytics") && !analytics && (
          <p className="text-sm text-ivory/40">Loading analytics…</p>
        )}

        {tab === "overview" && analytics && (
          <>
            <div className="grid grid-cols-2 gap-4 lg:grid-cols-4" data-testid="overview-stats">
              <StatCard label="Visitors Today" value={analytics.visitors_today} testid="stat-visitors-today" />
              <StatCard label="Visitors This Week" value={analytics.visitors_week} testid="stat-visitors-week" />
              <StatCard label="Visitors This Month" value={analytics.visitors_month} testid="stat-visitors-month" />
              <StatCard label="Total Page Views" value={analytics.total_views} testid="stat-total-views" />
            </div>
            <SectionCard title="Traffic Trend — Last 30 Days" testid="overview-trend">
              {analytics.total_views === 0 ? (
                <EmptyNote text="Tracking is live. Real visitor numbers will appear here as people browse the website — no dummy data." />
              ) : (
                <div className="h-56">
                  <ResponsiveContainer width="100%" height="100%">
                    <AreaChart data={analytics.trend} margin={{ left: -22, right: 6, top: 6 }}>
                      <XAxis dataKey="day" tick={{ fill: "#8f8779", fontSize: 10 }} tickFormatter={(d) => d.slice(5)} stroke="#3a362f" />
                      <YAxis tick={{ fill: "#8f8779", fontSize: 10 }} stroke="#3a362f" allowDecimals={false} />
                      <Tooltip contentStyle={{ background: "#242321", border: "1px solid #3a362f", borderRadius: 10, fontSize: 12 }} labelStyle={{ color: "#E7D7C0" }} />
                      <Area type="monotone" dataKey="visitors" stroke={GOLD} fill={GOLD} fillOpacity={0.15} strokeWidth={2} name="Visitors" />
                      <Area type="monotone" dataKey="views" stroke="#8f8779" fill="transparent" strokeWidth={1.5} strokeDasharray="4 4" name="Page views" />
                    </AreaChart>
                  </ResponsiveContainer>
                </div>
              )}
            </SectionCard>
            <SectionCard title="Top Visited Pages" testid="overview-top-pages">
              {analytics.top_pages.length === 0 ? (
                <EmptyNote text="No page views recorded yet." />
              ) : (
                <ul className="divide-y divide-ivory/8">
                  {analytics.top_pages.map((p) => (
                    <li key={p.path} className="flex items-center justify-between py-3 text-sm">
                      <span className="text-ivory/80">{p.path === "/" ? "Home (/)" : p.path}</span>
                      <span className="font-semibold text-champagne">{p.views}</span>
                    </li>
                  ))}
                </ul>
              )}
            </SectionCard>
          </>
        )}

        {tab === "analytics" && analytics && (
          <>
            <div className="flex gap-2" role="tablist" aria-label="Date range">
              {[7, 30, 90].map((d) => (
                <button
                  key={d}
                  onClick={() => setDays(d)}
                  data-testid={`range-${d}`}
                  className={`rounded-full px-4 py-2 text-xs font-semibold transition-colors ${
                    days === d ? "bg-champagne text-ink" : "border border-ivory/15 text-ivory/55 hover:text-ivory"
                  }`}
                >
                  {d} days
                </button>
              ))}
            </div>
            <div className="grid grid-cols-2 gap-4 lg:grid-cols-3">
              <StatCard label={`Unique Visitors · ${days}d`} value={analytics.unique_visitors} testid="stat-unique-visitors" />
              <StatCard label={`Page Views · ${days}d`} value={analytics.total_views} testid="stat-page-views" />
              <StatCard label="Visitors Today" value={analytics.visitors_today} testid="stat-analytics-today" />
            </div>
            <div className="grid gap-5 lg:grid-cols-2">
              <SectionCard title="Device Type" testid="analytics-devices">
                {analytics.devices.length === 0 ? (
                  <EmptyNote text="No visits recorded yet." />
                ) : (
                  <ul className="space-y-3">
                    {analytics.devices.map((d) => {
                      const max = Math.max(...analytics.devices.map((x) => x.count));
                      return (
                        <li key={d.device} data-testid={`device-${d.device}`}>
                          <div className="flex justify-between text-xs">
                            <span className="capitalize text-ivory/75">{d.device}</span>
                            <span className="font-semibold text-champagne">{d.count}</span>
                          </div>
                          <div className="mt-1.5 h-1.5 rounded-full bg-ivory/10">
                            <div className="h-1.5 rounded-full bg-champagne" style={{ width: `${(d.count / max) * 100}%` }} />
                          </div>
                        </li>
                      );
                    })}
                  </ul>
                )}
              </SectionCard>
              <SectionCard title="Traffic Sources" testid="analytics-referrers">
                {analytics.referrers.length === 0 ? (
                  <EmptyNote text="No external referrers recorded yet — most visitors arrive directly." />
                ) : (
                  <ul className="divide-y divide-ivory/8">
                    {analytics.referrers.map((r) => (
                      <li key={r.source} className="flex items-center justify-between py-3 text-sm">
                        <span className="truncate text-ivory/80">{r.source}</span>
                        <span className="font-semibold text-champagne">{r.count}</span>
                      </li>
                    ))}
                  </ul>
                )}
              </SectionCard>
            </div>
          </>
        )}

        {tab === "leads" && (
          <div data-testid="leads-list">
            {!leads ? (
              <p className="text-sm text-ivory/40">Loading enquiries…</p>
            ) : leads.length === 0 ? (
              <EmptyNote text="No enquiries yet. New booking requests from the website will appear here." />
            ) : (
              <ul className="space-y-4">
                {leads.map((l) => (
                  <li
                    key={l.id}
                    data-testid={`lead-${l.id}`}
                    className={`rounded-2xl border p-5 transition-colors ${
                      l.read ? "border-ivory/8 bg-white/[0.02]" : "border-champagne/30 bg-champagne/[0.05]"
                    }`}
                  >
                    <div className="flex flex-wrap items-start justify-between gap-3">
                      <div className="min-w-0">
                        <div className="flex items-center gap-2.5">
                          <p className="font-medium text-ivory">{l.name}</p>
                          {!l.read && (
                            <span className="rounded-full bg-champagne px-2 py-0.5 text-[9px] font-bold uppercase tracking-wider text-ink">New</span>
                          )}
                        </div>
                        <p className="mt-0.5 text-xs text-ivory/45">
                          {new Date(l.created_at).toLocaleString("en-IN", { day: "numeric", month: "short", hour: "numeric", minute: "2-digit" })}
                          {l.service ? ` · ${l.service}` : ""}
                          {l.date || l.time ? ` · Prefers ${[l.date, l.time].filter(Boolean).join(" at ")}` : ""}
                        </p>
                      </div>
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => markRead(l.id, !l.read)}
                          data-testid={`lead-read-${l.id}`}
                          aria-label={l.read ? "Mark as unread" : "Mark as read"}
                          className="flex h-9 w-9 items-center justify-center rounded-full border border-ivory/15 text-ivory/60 transition-colors hover:border-champagne hover:text-champagne"
                        >
                          {l.read ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                        </button>
                        <button
                          onClick={() => removeLead(l.id)}
                          data-testid={`lead-delete-${l.id}`}
                          aria-label="Delete enquiry"
                          className="flex h-9 w-9 items-center justify-center rounded-full border border-ivory/15 text-ivory/60 transition-colors hover:border-red-400 hover:text-red-300"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    </div>
                    <div className="mt-3 flex flex-wrap items-center gap-2">
                      <a href={`tel:+91${l.phone.replace(/\D/g, "").slice(-10)}`} data-testid={`lead-call-${l.id}`} className="inline-flex items-center gap-1.5 rounded-full border border-ivory/15 px-3.5 py-1.5 text-xs text-ivory/70 transition-colors hover:border-champagne hover:text-champagne">
                        <Phone className="h-3 w-3" /> {l.phone}
                      </a>
                      <a
                        href={waLink(`Hello ${l.name}, this is Glam Girl Makeup Studio regarding your enquiry${l.service ? ` about ${l.service}` : ""}.`)}
                        target="_blank"
                        rel="noopener noreferrer"
                        data-testid={`lead-whatsapp-${l.id}`}
                        className="inline-flex items-center gap-1.5 rounded-full border border-ivory/15 px-3.5 py-1.5 text-xs text-ivory/70 transition-colors hover:border-champagne hover:text-champagne"
                      >
                        <MessageCircle className="h-3 w-3" /> WhatsApp
                      </a>
                    </div>
                    {l.message && <p className="mt-3 border-t border-ivory/8 pt-3 text-sm leading-relaxed text-ivory/60">“{l.message}”</p>}
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}

        {tab === "website" && (
          <div className="grid gap-5 lg:grid-cols-2" data-testid="website-tab">
            <SectionCard title="Website Status" testid="website-status">
              <div className="flex items-center gap-3">
                <span className="relative flex h-3 w-3">
                  <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-400 opacity-60" />
                  <span className="relative inline-flex h-3 w-3 rounded-full bg-emerald-400" />
                </span>
                <p className="text-sm text-ivory/80">Online — dashboard and API responding</p>
              </div>
              <dl className="mt-5 space-y-3 text-sm">
                <div className="flex justify-between gap-4"><dt className="text-ivory/45">Business</dt><dd className="text-ivory/85">{BUSINESS.name}</dd></div>
                <div className="flex justify-between gap-4"><dt className="text-ivory/45">Hours</dt><dd className="text-ivory/85">{BUSINESS.hoursLine}</dd></div>
                <div className="flex justify-between gap-4"><dt className="text-ivory/45">Google rating</dt><dd className="text-ivory/85">{BUSINESS.rating} ★ · {BUSINESS.reviewCount} reviews</dd></div>
              </dl>
            </SectionCard>
            <SectionCard title="Quick Links" testid="website-links">
              <div className="space-y-3">
                <a
                  href="/"
                  data-testid="website-public-link"
                  className="flex items-center justify-between rounded-xl border border-ivory/12 px-5 py-4 text-sm text-ivory/80 transition-colors hover:border-champagne hover:text-champagne"
                >
                  Return to public website <ExternalLink className="h-4 w-4" />
                </a>
                <a
                  href={window.location.origin}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="website-live-link"
                  className="flex items-center justify-between rounded-xl border border-ivory/12 px-5 py-4 text-sm text-ivory/80 transition-colors hover:border-champagne hover:text-champagne"
                >
                  Open live website in new tab <ExternalLink className="h-4 w-4" />
                </a>
                <a
                  href={BUSINESS.mapsUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="website-maps-link"
                  className="flex items-center justify-between rounded-xl border border-ivory/12 px-5 py-4 text-sm text-ivory/80 transition-colors hover:border-champagne hover:text-champagne"
                >
                  Google Maps listing <ExternalLink className="h-4 w-4" />
                </a>
              </div>
            </SectionCard>
          </div>
        )}
      </div>
    </main>
  );
}
